<template>
  <SubscriptionConsumptionHistoryTable
    :title="$t(`${TRANSLATION_KEY_PREFIX}.title`)"
    :headers="headers"
    :rows="rows"
    :search="search"
    :pending="subscriptionDrPpuHistoryPending"
    :error="!!subscriptionDrPpuHistoryError"
    :error-text="$t(`${TRANSLATION_KEY_PREFIX}.error`)"
    :empty-title="$t(`${TRANSLATION_KEY_PREFIX}.${hasSearch ? 'noResults' : 'emptyState.title'}`)"
    data-locator="subscription-consumption-datarooms-history"
    row-locator="datarooms-history-row"
    :pagination-key="SUBSCRIPTION_DR_PPU_HISTORY_ITEMS_PER_PAGE"
  />
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { mapState, mapActions } from 'vuex'

  import { formatPrice } from '@/common/utils/currency'
  import { getDataroomStorageSize } from '@/common/utils/datarooms'
  import { ISOToDDMMYYYY } from '@/common/utils/dates'
  import { toast } from '@/common/utils/toast'
  import { GET_SUBSCRIPTION_DR_PPU_HISTORY } from '@/store/modules/subscription/action_types'
  import { SUBSCRIPTION_DR_PPU_HISTORY_ITEMS_PER_PAGE } from '@/subscriptions/constants.js'
  import SubscriptionConsumptionHistoryTable from '@/subscriptions/views/SubscriptionConsumptionHistoryTable.vue'
  import type { SubscriptionDrPpuHistoryItem } from '@/types/subscription'

  const TRANSLATION_KEY_PREFIX = 'subscriptions.views.SubscriptionConsumption.DataroomsHistory'

  export default defineComponent({
    name: 'SubscriptionConsumptionDrPpuHistory',
    components: { SubscriptionConsumptionHistoryTable },
    props: {
      search: {
        type: String,
        default: '',
      },
    },
    data () {
      return {
        TRANSLATION_KEY_PREFIX,
        SUBSCRIPTION_DR_PPU_HISTORY_ITEMS_PER_PAGE,
      }
    },
    computed: {
      ...mapState('subscription', [
        'subscriptionDrPpuHistory',
        'subscriptionDrPpuHistoryPending',
        'subscriptionDrPpuHistoryError',
      ]),
      hasSearch (): boolean {
        return this.search.trim().length > 0
      },
      headers () {
        return [
          { title: this.$t(`${TRANSLATION_KEY_PREFIX}.headers.date`), key: 'date', sortable: false },
          { title: this.$t(`${TRANSLATION_KEY_PREFIX}.headers.description`), key: 'description', sortable: false },
          { title: this.$t(`${TRANSLATION_KEY_PREFIX}.headers.dataroom`), key: 'dataroom', sortable: false },
          { title: this.$t(`${TRANSLATION_KEY_PREFIX}.headers.price`), key: 'price', sortable: false },
        ]
      },
      rows () {
        const history = (this.subscriptionDrPpuHistory ?? []) as SubscriptionDrPpuHistoryItem[]
        const query = this.search.trim().toLowerCase()
        return history
          .filter(item => !this.hasSearch || item.roomName.toLowerCase().includes(query))
          .sort((a, b) => (a.date === b.date ? b.id - a.id : b.date.localeCompare(a.date)))
          .map(item => ({
            id: item.id,
            date: ISOToDDMMYYYY(item.date),
            description: this.$t(`${TRANSLATION_KEY_PREFIX}.description`, { storage: this.storageLabel(item.additionalStorageGb) }),
            dataroom: item.roomName,
            price: formatPrice(item.amountNet),
          }))
      },
    },
    created () {
      this.getSubscriptionDataroomsPayPerUseHistory()
    },
    methods: {
      ...mapActions('subscription', [GET_SUBSCRIPTION_DR_PPU_HISTORY]),
      async getSubscriptionDataroomsPayPerUseHistory () {
        try {
          await this.GET_SUBSCRIPTION_DR_PPU_HISTORY()
        } catch {
          toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.error`))
        }
      },
      storageLabel (gb: number): string {
        const size = getDataroomStorageSize(gb)
        const unit = this.$t(`${TRANSLATION_KEY_PREFIX}.sizeUnit.${size.unit}`)
        return `${size.value} ${unit}`
      },
    },
  })
</script>
